'use client';

import { useCallback, useState } from 'react';
import { useTranslations } from 'next-intl';
import { ADDED_TO_BASKET_MODAL_ID, Locale } from '@headless-workspace/config';
import { unwrapResultSuccess } from '@headless-workspace/core-domain';
import { useToasts, useUIContext } from '@headless-workspace/core-ui';
import { ProductTile } from '@headless-workspace/glow-ds';
import { ActionStatus, useBasketMutations } from '../..';
import type { VipBrandsValue } from '../../../server';
import { useProductModalDisplay } from './useProductModalDisplay';

type UseProductBasketActionsParams = {
    vipBrands: VipBrandsValue;
    locale: Locale;
};

export const useAddToBasketMonoSkuBusinessRules = ({ vipBrands, locale }: UseProductBasketActionsParams) => {
    const { add: addToBasket, isMutating: isAddingToBasket } = useBasketMutations();
    const {
        menuModal: { close: closeMenuModal },
    } = useUIContext();

    const t = useTranslations('Discover.Pdp');
    const { notifySuccess, notifyError } = useToasts();

    const { displayBasketModal, isBasketModalOpen } = useProductModalDisplay({ vipBrands, locale });
    const [currentSku, setCurrentSku] = useState<string | null>(null);

    const handleAddToBasket = useCallback(
        async (product: ProductTile) => {
            if (isAddingToBasket) return;

            try {
                setCurrentSku(product.sku);

                const addToBasketResult = await addToBasket({ id: product.sku });
                const customerBasket = unwrapResultSuccess(addToBasketResult);

                if (customerBasket) {
                    const productAddedToBasket = customerBasket?.products.find(
                        (basketProduct) => basketProduct.productId === product.sku,
                    );

                    if (productAddedToBasket && !isBasketModalOpen) {
                        displayBasketModal(ActionStatus.AddToBasketCondition, productAddedToBasket, [product.sku]);
                    } else {
                        closeMenuModal(ADDED_TO_BASKET_MODAL_ID, true);
                        notifySuccess(t('information.basket.toast.label.success'));
                    }
                } else {
                    throw new Error();
                }
            } catch (error) {
                closeMenuModal(ADDED_TO_BASKET_MODAL_ID, true);
                notifyError(t('information.basket.toast.label.error'));
            } finally {
                setCurrentSku(null);
            }
        },
        [
            isAddingToBasket,
            addToBasket,
            isBasketModalOpen,
            displayBasketModal,
            closeMenuModal,
            notifySuccess,
            t,
            notifyError,
        ],
    );

    return {
        handleAddToBasket,
        currentSku,
        isAddingToBasket: (sku: string) => isAddingToBasket && currentSku === sku,
    };
};
